import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiArrowRight, FiPause, FiPlay } from 'react-icons/fi';
import EditorialLink from './EditorialLink';
import '../../styles/experience-spotlight.css';

export const EXPERIENCE_SPOTLIGHTS = [
  {
    id: 'oasis', title: "Oasis Live '27", short: 'Oasis', date: '2027 / UK, Europe, USA & Ireland',
    image: '/assets/community/oasis-live-2027.webp', alt: "Liam and Noel Gallagher, Oasis Live '27 artwork",
    position: 'center 38%',
    description: 'The songs. The stadiums. The summer. Plan your Oasis trip with HLT.',
    note: 'General sale sold out. Official resale information is available from Oasis.',
    cta: 'Enquire with HLT', source: 'https://oasisinet.com/',
  },
  {
    id: 'finsbury', title: 'Josh Baker b2b KETTAMA b2b Prospa', short: 'Finsbury Park',
    date: '2 July 2027 / Finsbury Park, London',
    image: '/assets/community/josh-baker-kettama-prospa-2027.webp',
    alt: 'Josh Baker, KETTAMA and Prospa event artwork', position: 'center 25%',
    description: 'Three names. One shared booth. Make a London summer day of it with HLT.',
    note: 'Ticket and hospitality availability confirmed personally on enquiry.',
    cta: 'Enquire about tickets',
    source: 'https://www.ticketmaster.co.uk/josh-baker-b2b-kettama-b2b-prospa-london-02-07-2027/event/3700653DD4D752CA',
  },
  {
    id: 'glastonbury', title: 'Glastonbury 2027', short: 'Glastonbury',
    date: '23-27 June 2027 / Worthy Farm, Somerset',
    image: 'https://media.timeout.com/images/105580321/750/562/image.jpg',
    alt: 'Flags and festival crowds at the Glastonbury Pyramid Stage', position: 'center 42%',
    description: 'Five days at Worthy Farm. Speak to HLT about your festival travel and stay.',
    note: 'General sale sold out. Festival admission is separate and must be booked through official channels.',
    cta: 'Plan your trip with HLT', source: 'https://www.glastonburyfestivals.co.uk/',
  },
];

export default function ExperienceSpotlight() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [hidden, setHidden] = useState(false);
  const touchStart = useRef(null);
  const current = EXPERIENCE_SPOTLIGHTS[index];

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncMotion = () => setReducedMotion(media.matches);
    const syncVisibility = () => setHidden(document.hidden);
    syncMotion();
    syncVisibility();
    media.addEventListener('change', syncMotion);
    document.addEventListener('visibilitychange', syncVisibility);
    return () => {
      media.removeEventListener('change', syncMotion);
      document.removeEventListener('visibilitychange', syncVisibility);
    };
  }, []);

  const rotating = !paused && !hovered && !focused && !reducedMotion && !hidden;
  useEffect(() => {
    if (!rotating) return undefined;
    const timer = setInterval(() => setIndex(value => (value + 1) % EXPERIENCE_SPOTLIGHTS.length), 7000);
    return () => clearInterval(timer);
  }, [rotating, index]);

  function move(amount) {
    setIndex(value => (value + amount + EXPERIENCE_SPOTLIGHTS.length) % EXPERIENCE_SPOTLIGHTS.length);
  }

  return <section className={`hlt-experience-spotlight hlt-spotlight-${current.id}`} aria-label="Featured experiences" aria-roledescription="carousel"
    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
    onFocusCapture={() => setFocused(true)} onBlurCapture={e => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
    onTouchStart={e => { touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }}
    onTouchEnd={e => {
      if (!touchStart.current) return;
      const dx = e.changedTouches[0].clientX - touchStart.current.x;
      const dy = e.changedTouches[0].clientY - touchStart.current.y;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { move(dx < 0 ? 1 : -1); setPaused(true); }
      touchStart.current = null;
    }}>
    <div className="hlt-spotlight-stage">
      {EXPERIENCE_SPOTLIGHTS.map((event, i) => <img key={event.id} className={`hlt-spotlight-photo${index === i ? ' is-active' : ''}`}
        src={event.image} alt={index === i ? event.alt : ''} aria-hidden={index !== i}
        style={{ objectPosition: event.position }} loading={i === 0 ? 'eager' : 'lazy'} fetchPriority={i === 0 ? 'high' : undefined} />)}
      <div className="hlt-spotlight-copy" aria-live={rotating ? 'off' : 'polite'} aria-atomic="true">
        <p className="hlt-eyebrow">Experiences / In the spotlight</p>
        <p className="hlt-spotlight-date">{current.date}</p>
        <h1>{current.title}</h1>
        <p className="hlt-spotlight-description">{current.description}</p>
        <div className="hlt-spotlight-actions">
          <EditorialLink to="https://wa.me/message/5UT3QJZMUBKNP1" className="hlt-primary">Inquire for Tickets</EditorialLink>
        </div>
        <p className="hlt-spotlight-note">{current.note}</p>
      </div>
    </div>
    <div className="hlt-spotlight-footer">
      <div className="hlt-spotlight-selector" aria-label="Choose featured event">
        {EXPERIENCE_SPOTLIGHTS.map((event, i) => <button type="button" key={event.id}
          aria-label={`Show ${event.title}`} aria-pressed={index === i} onClick={() => setIndex(i)}>
          <span>0{i + 1}</span><span className="hlt-spotlight-selector-name">{event.short}</span>
        </button>)}
      </div>
      <div className="hlt-spotlight-controls">
        <button type="button" title="Previous event" aria-label="Previous event" onClick={() => move(-1)}><FiArrowLeft /></button>
        {!reducedMotion && <button type="button" title={paused ? 'Resume rotation' : 'Pause rotation'} aria-label={paused ? 'Resume rotation' : 'Pause rotation'} onClick={() => setPaused(value => !value)}>{paused ? <FiPlay /> : <FiPause />}</button>}
        <button type="button" title="Next event" aria-label="Next event" onClick={() => move(1)}><FiArrowRight /></button>
      </div>
    </div>
  </section>;
}
